import React from 'react';
import { useSelector } from 'react-redux';

import { L10nNumber } from '@travel/i18n';
import { RakutenPoints } from '@travel/icons/ui';
import Translate from '@travel/traveler-core/components/Translate';
import { PROMOTION_CLASS } from '@travel/traveler-core/constants/salesPromotion';
import { MOBILE_SCREEN, useDeviceType, usePriceLabel } from '@travel/traveler-core/hooks';
import { PlanPaymentRestrictionFeature, Price } from '@travel/traveler-core/types/providerRatePlan';
import { shouldDisplayDiscountPrice } from '@travel/traveler-core/utils/salesPromotion';
import { Tag } from '@travel/ui';
import { isNotEmptyArray } from '@travel/utils';

import Money from 'components/Money';
import PlanPaymentRestriction from 'components/PlanPaymentRestriction';
import PriceFor from 'components/PriceFor';
import DirectPromotionBadge from 'components/PriceLabel/DirectPromotionBadge';
import {
  InternalSalesPromotionFormQuery,
  SalesPromotionFormQuery,
} from 'components/SalesPromotionInput';
import { RoomFormQuery } from 'pages/ProviderListPage/components/SearchForm';

import { getMember } from 'store/member/selectors';

import ReserveButton from '../ReserveButton';

import styles from './roomPlanItemCard.module.scss';

type Props = {
  price: Price;
  points?: {
    amount: number;
    currency: string;
  };
  paymentRestrictionFeature?: PlanPaymentRestrictionFeature;
  searchParam: RoomFormQuery & SalesPromotionFormQuery & InternalSalesPromotionFormQuery;
  onReserve: () => void;
  onSelectDate?: (startDate?: string, endDate?: string) => void;
  itemRatePlanId: string;
};

function PriceInfo(props: Props) {
  const { searchParam, price, points, paymentRestrictionFeature, onReserve, onSelectDate } = props;

  const isMobile = useDeviceType() === MOBILE_SCREEN;
  const { isMember } = useSelector(getMember);

  const { guestLabel, nightLabel, chargeUnitLabel } = usePriceLabel(searchParam);

  const taxesLabel = (
    <Translate
      className={styles.label}
      id={
        price?.isConsumptionTaxIncluded
          ? 'Provider_List.Search_Results.Taxes'
          : 'Provider_List.Luxury.Tax_Excluded'
      }
    />
  );

  let basePrice = null;
  let totalPrice = price?.base;

  if (
    isNotEmptyArray(price?.salesPromotions) &&
    shouldDisplayDiscountPrice(price.salesPromotions)
  ) {
    basePrice = price?.base;
    totalPrice = price?.total;
  }

  const mainSalesPromotion = price.salesPromotions?.find(
    sp => sp.promotionClass === PROMOTION_CLASS.MAIN,
  );
  const subSalesPromotion = price.salesPromotions?.find(
    sp => sp.promotionClass === PROMOTION_CLASS.SUB,
  );
  const isSameSalesPromotionRepresentativeName =
    mainSalesPromotion &&
    subSalesPromotion &&
    mainSalesPromotion.representativeFeatureName === subSalesPromotion.representativeFeatureName;

  const priceAndTax = (
    <div className={styles.priceAndTax}>
      {isNotEmptyArray(price.salesPromotions) && basePrice && basePrice !== totalPrice && (
        <div className={styles.basePriceWrapper}>
          <span className={styles.basePrice} data-testid="priceInfo-basePrice">
            <Money value={basePrice} className={styles.priceText} />
          </span>

          {!!price.discountRate && price.discountRate >= 2 && (
            <Tag
              type="filled"
              color="scarlet"
              className={styles.discountTag}
              data-testid="priceLabel-discountRate-tag"
            >
              {<>-{price.discountRate}%</>}
            </Tag>
          )}
        </div>
      )}

      <div className={styles.totalPrice} data-testid="priceInfo-totalPrice">
        <Money value={totalPrice} className={styles.priceText} />
      </div>
      {taxesLabel}
    </div>
  );

  const promotionTags = isNotEmptyArray(price.salesPromotions) && (
    <div className={styles.tagsWrapper}>
      {mainSalesPromotion && (
        <DirectPromotionBadge
          key={'main'}
          className={styles.directPromotion}
          salesPromotion={mainSalesPromotion}
          planPaymentRestrictionFeature={paymentRestrictionFeature}
        />
      )}
      {subSalesPromotion && !isSameSalesPromotionRepresentativeName && (
        <DirectPromotionBadge
          className={styles.directPromotion}
          key={'sub'}
          salesPromotion={subSalesPromotion}
        />
      )}
    </div>
  );

  return (
    <>
      {isMobile && promotionTags}
      <div className={styles.itemPriceWrapper}>
        <div className={styles.priceInfoWrapper}>
          <div className={styles.priceLeft}>
            {!isMobile && promotionTags}

            {!isMobile && (
              <div className={styles.restrictionAndPoints}>
                {paymentRestrictionFeature && (
                  <Tag type="filled" color="gray" className={styles.paymentRestrictionTag}>
                    <PlanPaymentRestriction
                      className={styles.paymentRestriction}
                      {...paymentRestrictionFeature}
                    />
                  </Tag>
                )}
                {Number(points?.amount) > 0 && (
                  <span className={styles.pointItem}>
                    <RakutenPoints size={16} />
                    <Translate
                      id="Provider_Info.Room_List.Plan_Info.Points"
                      data={{
                        points_calculation: <L10nNumber value={points?.amount || 0} />,
                        currency: `${points?.currency || ''}`,
                      }}
                    />
                  </span>
                )}
              </div>
            )}

            {isMobile && priceAndTax}
            {Boolean(chargeUnitLabel) && (
              <div className={styles.label} data-testid="priceLabel-chargeUnit-label">
                {chargeUnitLabel}
              </div>
            )}
            {Boolean(guestLabel && nightLabel) && (
              <div className={styles.label}>
                <PriceFor
                  rooms={Number(searchParam.noOfUnits)}
                  adults={Number(searchParam.adults)}
                  childrenAges={
                    props.searchParam?.childrenAges
                      ? props.searchParam?.childrenAges.split(',').map(Number)
                      : []
                  }
                  startDate={searchParam.startDate}
                  endDate={searchParam.endDate}
                  shouldShowIcon={!isMobile}
                  title={
                    <>
                      <Translate id="Provider_Info.Basic_Info.Hotel_Info.Price_For" />
                      &nbsp;
                    </>
                  }
                />
              </div>
            )}
            {isMobile && paymentRestrictionFeature && (
              <Tag type="filled" color="gray" className={styles.paymentRestrictionTag}>
                <PlanPaymentRestriction
                  className={styles.paymentRestriction}
                  {...paymentRestrictionFeature}
                />
              </Tag>
            )}
            {isMobile && Number(points?.amount) > 0 && (
              <span className={styles.pointItem}>
                <RakutenPoints size={16} />
                <Translate
                  id="Provider_Info.Room_List.Plan_Info.Points"
                  data={{
                    points_calculation: <L10nNumber value={points?.amount || 0} />,
                    currency: `${points?.currency || ''}`,
                  }}
                />
              </span>
            )}
          </div>
          {!isMobile && priceAndTax}
          {isMobile && !isMember && (
            <Translate
              className={styles.remindMessage}
              id="Provider_Info.Reserve.No_Account_Needed"
            />
          )}
        </div>
        <div className={styles.reserveWrapper}>
          <ReserveButton
            wrapperClassName={styles.reserveButtonWrapper}
            className={styles.reserveButton}
            onClick={onReserve}
            searchParam={searchParam}
            onSelectDate={onSelectDate}
            shouldShowVacancyCalendar={true}
            itemRatePlanId={props.itemRatePlanId}
          />
        </div>
      </div>
    </>
  );
}

export default PriceInfo;
